/**
 * Tournament display helpers.
 *
 * Same Tournament entity is rendered on 4 pages (player browse, player
 * detail, business list, business detail). Without a single source of
 * truth, each page formats dates / capacity / status its own way and the
 * UI drifts. Every helper here returns the canonical string for one piece
 * of tournament data — all 4 pages MUST use these so the same row of data
 * looks identical no matter where it's shown.
 */

import { playerOriginForBusinessOrigin } from "./playerAppLinks";

const MONTH_DAY_YEAR: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const WEEKDAY_LONG: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
};

/** Use en-US explicitly so server-rendered tests + browsers agree. */
const LOCALE = "en-US";

/**
 * Canonical short date — "Apr 24, 2026".
 *
 * Used for single-date display (e.g. registration deadline, end-only
 * fallback). Browser pages that need just the start date should call
 * `formatTournamentLongDate` instead so the detail page stays distinct.
 */
export function formatTournamentShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(LOCALE, MONTH_DAY_YEAR);
}

/**
 * Canonical long date — "Friday, April 24, 2026".
 *
 * Used on detail pages where the date is a hero element and deserves
 * the full weekday + month treatment.
 */
export function formatTournamentLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString(LOCALE, WEEKDAY_LONG);
}

/**
 * Canonical capacity string — "32 players max".
 *
 * Replaces the previous "Max 32 players" / "32 players max" /
 * "{n} player cap" variants.
 */
export function formatTournamentCapacity(maxPlayers: number): string {
  return `${maxPlayers} players max`;
}

/**
 * Canonical "Goodminton Fremont · Fremont, CA" facility line for the
 * cross-facility browse experience. Region is omitted when both city and
 * state are null/empty.
 */
export function formatTournamentFacilityLine(
  facilityName: string,
  facilityCity?: string | null,
  facilityState?: string | null,
): string {
  const region = [facilityCity, facilityState]
    .filter((v): v is string => Boolean(v))
    .join(", ");
  return region ? `${facilityName} · ${region}` : facilityName;
}

/**
 * Canonical label for the persisted ORM `status` field
 * (`draft|open|pending|in_progress|completed|cancelled`).
 *
 * The player browser derives a richer lifecycle status from dates — that
 * helper lives in TournamentBrowserPage.tsx and is intentionally NOT
 * replaced here (it's the subject of an in-flight PR). This helper is for
 * the operator-set status that business pages and the player detail page
 * surface verbatim.
 */
export function formatTournamentStatusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "open":
      return "Open";
    case "pending":
      return "Pending";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/**
 * Convert a known Business origin into the corresponding Player origin for a
 * one-time venue-display setup link. Unknown origins deliberately return null
 * rather than guessing a host that could send a capability to the wrong app.
 */
export function buildVenueDisplaySetupUrl(
  businessOrigin: string,
  exchangeReference: string,
): string | null {
  if (!exchangeReference.trim()) return null;
  const playerOrigin = playerOriginForBusinessOrigin(businessOrigin);
  if (!playerOrigin) return null;
  const target = new URL("/tournament-display", playerOrigin);
  // A one-time display reference is a bearer credential. Fragments stay out of
  // the initial HTTP request and Referer header while remaining available to
  // the chrome-less exchange page before it scrubs browser history.
  target.hash = new URLSearchParams({ ref: exchangeReference }).toString();
  return target.toString();
}
